import React, { useState, useMemo } from 'react';
import { Card, CardBody, LoadingSpinner } from '../common';
import { PassageDisplay } from './PassageDisplay';
import { useAllPassages } from '../../hooks/usePassages';

export const StoryViewer = ({ gameId }) => {
  const [selectedScene, setSelectedScene] = useState(null);
  const [showDetails, setShowDetails] = useState(true);
  // Always fetch all passages to build the complete scene list
  const { data, isLoading, error } = useAllPassages(gameId, null);

  const allPassages = data?.passages || [];

  // Build scenes map from ALL passages (not filtered)
  // Must be called before any early returns to satisfy hooks rules
  const scenes = useMemo(() => {
    const sceneMap = new Map();
    allPassages.forEach(p => {
      if (p.scene) {
        const sceneKey = p.scene.id;
        if (!sceneMap.has(sceneKey)) {
          sceneMap.set(sceneKey, {
            ...p.scene,
            passages: []
          });
        }
        sceneMap.get(sceneKey).passages.push(p);
      }
    });
    return sceneMap;
  }, [allPassages]);

  // Filter passages based on selected scene (local filtering)
  const passages = useMemo(() => {
    if (!selectedScene) {
      return allPassages;
    }
    return allPassages.filter(p => p.scene?.id === selectedScene);
  }, [allPassages, selectedScene]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        <p>Failed to load story: {error.message}</p>
      </div>
    );
  }

  if (allPassages.length === 0) {
    return (
      <Card>
        <CardBody>
          <div className="text-center py-8 text-gray-500">
            <svg
              className="w-12 h-12 mx-auto mb-4 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-700 mb-2">The Story Begins...</h3>
            <p>No passages have been published yet.</p>
            <p className="text-sm mt-2">Check back after the first round is complete.</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls row */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        {/* Scene tabs (if multiple scenes) */}
        {scenes.size > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2">
            <button
              className={`px-3 py-1.5 text-sm rounded-full whitespace-nowrap ${
                !selectedScene ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              onClick={() => setSelectedScene(null)}
            >
              All Scenes
            </button>
            {Array.from(scenes.values()).map(scene => (
              <button
                key={scene.id}
                className={`px-3 py-1.5 text-sm rounded-full whitespace-nowrap ${
                  selectedScene === scene.id
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                onClick={() => setSelectedScene(scene.id)}
              >
                {scene.title?.trim() || `Scene ${scene.number}`}
              </button>
            ))}
          </div>
        )}

        {/* Show details toggle */}
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showDetails}
            onChange={(e) => setShowDetails(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Show game details
        </label>
      </div>

      {/* Passages */}
      <Card>
        <CardBody>
          <div className="max-h-[600px] overflow-y-auto pr-2">
            {passages.map((passage, index) => (
              <React.Fragment key={passage.id}>
                <PassageDisplay
                  passage={passage}
                  showMeta={true}
                  showPrompt={showDetails}
                  showWinningDetail={showDetails}
                  showAspects={showDetails}
                  showAttribution={showDetails}
                />

                {/* Separator between passages */}
                {index < passages.length - 1 && (
                  <hr className="my-6 border-gray-200" />
                )}
              </React.Fragment>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* Stats */}
      <div className="text-sm text-gray-500 text-center">
        {passages.length} passage{passages.length !== 1 ? 's' : ''} published
        {selectedScene
          ? ' in this scene'
          : scenes.size > 0 && ` across ${scenes.size} scene${scenes.size !== 1 ? 's' : ''}`
        }
      </div>
    </div>
  );
};
